import { TermScheduleFlags } from '../services/term_schedule_flags';
import { ScheduledSectionStatusServant } from './scheduled_section_status_servant';
import { ScheduledSectionsQuery } from '../queries/scheduled_sections_query';
import { TermCourseServant } from '../../term_courses/servants/term_course_servant';
import { Section } from '../../sections/builders/section_builder';
import { ISectionsDatasource } from '../../sections/datasources/sections_datasource';
import { TermPlannedCourse } from '../../term_courses/builders/term_planned_course_builder';
import { ITermCoursesDatasource } from '../../term_courses/datasources/term_courses_datasource';
import {
  ITermCourse,
  ISection,
  IPusherOutcome,
  IRegistrationWaitlistPusherOutcome,
  ICourse,
  ISectionType,
  IRegistrationSection,
  ICrossCourseCorequisite
} from '../../../interfaces/scheduler_interfaces';
import { MultiSectionServant } from './multi_section_servant';
import { ScheduledSectionServant } from './scheduled_section_servant';
import { RegistrationAlertMessages } from '../types/registration_alert_messages';
import { ScheduledSectionStatus } from '../types/scheduled_section_status_enum';
const _4 = require('lodash4');

export class RegistrationAlertsServant {
  private showingPending: boolean;
  private notificationDelegate: any;

  constructor(
    private ngTermScheduleFlags: TermScheduleFlags,
    private phxNotificationDelegate: any,
    private ngTermCoursesDatasource: ITermCoursesDatasource,
    private ngScheduledSectionServant: ScheduledSectionServant,
    private ngMultiSectionServant: MultiSectionServant,
    private ngSectionsDatasource: ISectionsDatasource,
    private ngTermCourseServant: TermCourseServant,
    private ngScheduledSectionsQuery: ScheduledSectionsQuery,
    private ngScheduledSectionStatusServant: ScheduledSectionStatusServant
  ) {
    this.showingPending = false;
    this.notificationDelegate = this.phxNotificationDelegate.restrictTo('reg-alerts');
  }

  showPendingAlert(pendingMessage = RegistrationAlertMessages.PENDING_DEFAULT_TEXT) {
    if (!this.showingPending) {
      this.notificationDelegate.info({
        content: pendingMessage,
        showIcon: true,
        name: 'PendingAlert',
        dismissOnTimeout: false,
        dismissButton: false,
        timeout: 10000,
        iconClass: 'eab-icon-sync'
      });
      this.showingPending = true;
    }
  }

  hidePendingAlert() {
    if (this.showingPending) {
      this.notificationDelegate.dismissAlertByName('PendingAlert');
      this.showingPending = false;
    }
  }

  getCoursesAsTemplate(courses: ITermCourse[]) {
    const coursesIds: number[] = _4.map(courses, 'id');

    const newCourses = _4.map(coursesIds, (courseId: number) => this.ngTermCoursesDatasource.get(courseId));

    const MAX_NUM_OF_CHAR_SECTION_TYPE = 18;
    const names = _4.map(newCourses, (course: ITermCourse) => {
      const _message = `${course.cd} ${course.title}`;
      return _4.template(`<p class="text-sz-m-sz-m mbz"> ${_message} </p>`)({
        course
      });
    });
    return names.join('');
  }

  getPluralText(courseArray: ITermCourse[]): string {
    return _4.size(courseArray) > 1 ? ' these courses:' : ' this course:';
  }

  createGenericNotification(content: string, status: string) {
    const config = {
      content,
      showIcon: true,
      status,
      dismissButton: true
    };
    if (status !== 'error') {
      config.dismissButton = false;
    }
    this.notificationDelegate.create(config);
  }
  createNotification(courseArray: ITermCourse[], status: any, message: string) {
    const contentTemplate = `<p class="text-sz-m-sz-m">${message}</p>`;
    this.createGenericNotification(`${contentTemplate} ${this.getCoursesAsTemplate(courseArray)}`, status);
  }
  createNonWaitlistNotification(coursesArray: ITermCourse[], status: string, message: string) {
    let composedMsg = message;
    if (!_4.isEmpty(coursesArray)) {
      composedMsg = composedMsg + this.getPluralText(coursesArray);
    }
    if (status === 'error') {
      composedMsg = _4.endsWith(composedMsg, ':') ? composedMsg : composedMsg + ' Try again';
    }
    this.createNotification(coursesArray, status, composedMsg);
  }
  invokeAlerts(course_reg_statuses: any, successMsg: string, failedMsg: string) {
    const courses_registered = course_reg_statuses.courses_registered;
    const courses_failed_to_register = course_reg_statuses.courses_failed_to_register;
    this.hidePendingAlert();
    if (!_4.isEmpty(courses_registered)) {
      this.createNonWaitlistNotification(courses_registered, 'success', successMsg);
    }
    if (!_4.isEmpty(courses_failed_to_register)) {
      this.createNonWaitlistNotification(courses_failed_to_register, 'error', failedMsg);
    }
  }
  updateDropAlerts(course_reg_statuses: any) {
    this.invokeAlerts(
      course_reg_statuses,
      RegistrationAlertMessages.SUCCESS_DROPPED_TEXT,
      RegistrationAlertMessages.FAILED_DROPPED_TEXT
    );
  }
  updateWaitlistRemoveAlerts(course_reg_statuses: any) {
    const courses_registered = course_reg_statuses.courses_registered;
    const courses_failed_to_register = course_reg_statuses.courses_failed_to_register;
    this.hidePendingAlert();
    if (!_4.isEmpty(courses_registered)) {
      this.createNonWaitlistNotification(
        courses_registered,
        'success',
        RegistrationAlertMessages.WAITLIST_REMOVE_SUCCESS_TEXT
      );
    }
    if (!_4.isEmpty(courses_failed_to_register)) {
      this.createNonWaitlistNotification(
        courses_failed_to_register,
        'error',
        RegistrationAlertMessages.WAITLIST_REMOVE_FAIL_TEXT
      );
    }
  }
  getActionRequiredForCourses(courses: any) {
    let content = '';
    const _courses = _4.uniq(courses);
    _courses.forEach((course: ITermCourse) => {
      if (course._has_multi_section_types) {
        const requiredAndWaitlistedSections =
          this.ngMultiSectionServant.getRequiredNonScheduledAndWaitlistedSections(course);

        if (requiredAndWaitlistedSections.length > 0) {
          const _courseWithoutRegisteredSections: any = [];
          requiredAndWaitlistedSections.forEach((section: any) => {
            const _course = _4.cloneDeep(course);
            _courseWithoutRegisteredSections.push(_course);
          });
          content += this.getCoursesAsTemplate(_courseWithoutRegisteredSections);
        }
      }
    });
    return content;
  }
  showPendingActionsForSection(courses: any) {
    const actions = this.getActionRequiredForCourses(courses);
    if (!_4.isEmpty(actions)) {
      this.notificationDelegate.info({
        content: `<p class='text-sz-m text-bold'> ${RegistrationAlertMessages.ADDITIONAL_SECTIONS_ARE_REQUIRED_TEXT} </p> ${actions}`,
        showIcon: true,
        dismissOnTimeout: false,
        iconClass: 'eab-icon-information'
      });
    }
  }
  showUnscheduleSectionSuccessAlert(sectionId: number) {
    const section = _4.find(this.ngSectionsDatasource.metasource().derived(), { id: sectionId });
    let content = `<p class="text-sz-m-sz-m text-bold">${RegistrationAlertMessages.SUCCESSFUL_UNSCHEDULED_TEXT}</p>`;

    if (section) {
      content += `${_4.get(section, '_course.cd', '')} ${_4.get(section, '_course.title', '')}`;
    }

    const config = {
      content,
      showIcon: true,
      status: 'info'
    };
    this.notificationDelegate.create(config);
  }
  showScheduledAlerts(sections: ISection[]) {
    const _sections: ISection[] = _4.filter(sections, (section: ISection) => {
      const sectionToCheck = this.ngSectionsDatasource.get(section.id);
      if (sectionToCheck) {
        return this.ngScheduledSectionStatusServant.isSectionScheduled(sectionToCheck);
      }
      return false;
    });
    this.showScheduledGrowler(_sections);
  }

  showScheduledGrowler(sections: ISection[]) {
    const contentTemplate = `<p class="text-sz-m-sz-m text-bold">${RegistrationAlertMessages.SUCCESSFUL_SCHEDULED_TEXT}</p>`;
    const alertMessage = sections.map((section) => {
      const isMultiSection = TermCourseServant.isMultiSectionCourse(section._course);
      const sectionType = isMultiSection ? this.ngMultiSectionServant.getTruncatedSectionTypeName(section, 18) : ``;
      return `${section._course.cd} ${section._course.title} ${sectionType}`;
    });
    const config = {
      content: `${contentTemplate} ${alertMessage}`,
      showIcon: true,
      status: 'success'
    };
    this.notificationDelegate.create(config);
  }
  showAddedCoreqAlerts(coreq: ITermCourse | ICrossCourseCorequisite) {
    this.showAddedCoreqGrowler(coreq);
  }

  showAddedCoreqGrowler(coreq: ITermCourse | ICrossCourseCorequisite) {
    const contentTemplate = `<p class="text-sz-m-sz-m text-bold">${RegistrationAlertMessages.SUCCESSFUL_ADDED_COREQ_TEXT}</p>`;
    const alertMessage = `${coreq.cd} ${coreq.title}`;
    const config = {
      content: `${contentTemplate} ${alertMessage}`,
      showIcon: true,
      status: 'success'
    };
    this.notificationDelegate.create(config);
  }
  updateAddAlerts(course_reg_statuses: any) {
    this.invokeAlerts(
      course_reg_statuses,
      RegistrationAlertMessages.SUCCESS_REGISTERED_TEXT,
      RegistrationAlertMessages.FAILED_REGISTERED_TEXT
    );
    const courses_registered = course_reg_statuses.courses_registered;
    if (!_4.isEmpty(courses_registered)) {
      this.notificationDelegate.info({
        content: `<p class="text-sz-m">${RegistrationAlertMessages.PAY_TUITION_TEXT}</p>`,
        showIcon: true,
        dismissOnTimeout: false
      });
    }
  }
  showWaitlistAlert(section: any, status: string, message: string) {
    const isError = status === 'error';
    const composedMsg = isError
      ? `${RegistrationAlertMessages.WAITLIST_FAIL_TEXT}`
      : `${RegistrationAlertMessages.WAITLIST_SUCCESS_TEXT}`;
    this.hidePendingAlert();
    const alertMessage = this.courseTemplate(section);
    const contentTemplate = `<p class="text-sz-m text-bold">${composedMsg} this course</p>`;

    const config = {
      content: `${contentTemplate} ${alertMessage} <p class="text-sz-m">${message}</p>`,
      showIcon: true,
      status,
      dismissButton: !isError
    };
    this.notificationDelegate.create(config);

    if (!isError && this.ngTermScheduleFlags.isBatchRegisterWaitlistedStudentsEnabled()) {
      this.showAutoRegisterWaitlistedReminder();
    }
  }
  showDropSuccessGrowler(dropPusherOutcome: IPusherOutcome[], isWaitlisted: boolean) {
    const alertMessage = this.courseTemplateFromPusherStatus(dropPusherOutcome);
    const pluralCourses = _4.size(dropPusherOutcome) > 1 ? ' these courses:' : ' this course:';
    const contentTemplate = `<p class="text-sz-m text-bold">${
      isWaitlisted
        ? RegistrationAlertMessages.WAITLIST_REMOVE_SUCCESS_TEXT
        : RegistrationAlertMessages.SUCCESS_DROPPED_TEXT
    } ${pluralCourses}</p>`;

    const config = {
      content: `${contentTemplate} ${alertMessage}`,
      showIcon: true,
      status: 'success',
      dismissButton: true
    };
    this.notificationDelegate.create(config);
  }
  showDropErrorGrowler(dropPusherOutcome: IPusherOutcome[], isWaitlisted: boolean) {
    const alertMessage = this.courseTemplateFromPusherStatus(dropPusherOutcome);
    const pluralCourses = _4.size(dropPusherOutcome) > 1 ? ' these courses:' : ' this course:';
    const contentTemplate = `<p class="text-sz-m text-bold">${
      isWaitlisted ? RegistrationAlertMessages.WAITLIST_REMOVE_FAIL_TEXT : RegistrationAlertMessages.FAILED_DROPPED_TEXT
    } ${pluralCourses}</p>`;

    const config = {
      content: `${contentTemplate} ${alertMessage}`,
      showIcon: true,
      status: 'error',
      dismissButton: false
    };
    this.notificationDelegate.create(config);
  }
  showSuccessRegistrationAlerts(registrationPusherOutcome: IPusherOutcome[]) {
    const alertMessage = this.courseTemplateFromPusherStatus(registrationPusherOutcome);
    const pluralCourses = _4.size(registrationPusherOutcome) > 1 ? ' these courses:' : ' this course:';
    const contentTemplate = `<p class="text-sz-m text-bold">${RegistrationAlertMessages.SUCCESS_REGISTERED_TEXT} ${pluralCourses}</p>`;

    const config = {
      content: `${contentTemplate} ${alertMessage}`,
      showIcon: true,
      status: 'success',
      dismissButton: true
    };
    this.notificationDelegate.create(config);
    // Show pay tuition alert
    if (!_4.isEmpty(registrationPusherOutcome)) {
      this.notificationDelegate.info({
        content: `<p class="text-sz-m">${RegistrationAlertMessages.PAY_TUITION_TEXT}</p>`,
        showIcon: true,
        dismissOnTimeout: false
      });
    }
  }
  showFailedRegistrationAlerts(registrationPusherOutcome: IPusherOutcome[]) {
    const alertMessage = this.courseTemplateFromPusherStatus(registrationPusherOutcome);
    const pluralCourses = _4.size(registrationPusherOutcome) > 1 ? ' these courses:' : ' this course:';
    const contentTemplate = `<p class="text-sz-m text-bold">${RegistrationAlertMessages.FAILED_REGISTERED_TEXT} ${pluralCourses}</p>`;

    const config = {
      content: `${contentTemplate} ${alertMessage}`,
      showIcon: true,
      status: 'error',
      dismissButton: false
    };
    this.notificationDelegate.create(config);
  }

  showAutoWaitlistSectionWaitlistedAlerts(registrationPusherOutcome: IPusherOutcome, waitlistPosition?: number) {
    const section = _4.find(this.ngScheduledSectionsQuery.result.list, (sec: any) =>
      _4.isEqual(sec.id, registrationPusherOutcome.id)
    );
    const waitlistPositionMsg =
      !_4.isUndefined(waitlistPosition) && _4.gt(waitlistPosition, 0)
        ? _4.gt(waitlistPosition, 1)
          ? `<p class="text-sz-m">There are ${waitlistPosition} students ahead of you.</p>`
          : `<p class="text-sz-m">You are first in line.</p>`
        : ``;
    const contentTemplate = `<p class="text-sz-m bold">${section._course.cd} ${section._course.title}</p>
                          <p class="text-sz-m"> ${RegistrationAlertMessages.AUTO_WAITLIST_WAITLISTED_TEXT}</p>
                          ${waitlistPositionMsg}`;
    const config = {
      content: `${contentTemplate}`,
      showIcon: true,
      status: 'warn',
      dismissButton: false
    };
    this.notificationDelegate.create(config);
  }

  showAutoWaitlistSectionRegisteredAlerts(registrationPusherOutcome: IPusherOutcome) {
    const section = _4.find(this.ngScheduledSectionsQuery.result.list, (sec: any) =>
      _4.isEqual(sec.id, registrationPusherOutcome.id)
    );
    const contentTemplate = `<p class="text-sz-m text-bold">Successfully registered</p>
     <p class="text-sz-m text-bold">${section._course.cd} ${section._course.title}</p> <p class="text-sz-m"> ${RegistrationAlertMessages.AUTO_WAITLIST_REGISTERED_TEXT}</p>`;
    const config = {
      content: `${contentTemplate}`,
      showIcon: true,
      status: 'success',
      dismissButton: false
    };
    this.notificationDelegate.create(config);
  }

  /* tslint:disable:cognitive-complexity */
  showOtherRequiredSectionTypesAlert(section: ISection) {
    let course = section._course;
    // from commands, usually they will be passing the ngSectionDatasource entry
    if (_4.isUndefined(course)) {
      const _section = _4.find(this.ngScheduledSectionsQuery.result.list, { id: section.id });
      if (_section) {
        course = _section._course;
      }
      // if we don't find section in the query, then there is a fundamental problem in the way this function got called
      // try to run the query before making the call to this function
    }
    const isMultiSection = TermCourseServant.isMultiSectionCourse(course);
    if (!isMultiSection) {
      return;
    }
    const sectionTypesForCourse = _4.sortBy(
      _4.filter(
        course._section_types,
        (sectionType: ISectionType) => sectionType.session === section._section_type.session
      ),
      'rank'
    );

    const scheduledSectionsForCourse = _4.filter(
      this.ngScheduledSectionsQuery.result.list,
      (schedSection: ISection) =>
        schedSection.course === course.id && schedSection._section_type.session === section._section_type.session
    );
    const scheduledSectionTypes = _4.map(scheduledSectionsForCourse, 'section_type');

    const unScheduledSectionTypeIDsForCourse = _4(sectionTypesForCourse)
      .reject((sectionType: ISectionType) => _4.includes(scheduledSectionTypes, sectionType.id))
      .map('id')
      .value();

    // it will not have section which is just waitlisted
    const waitListedSectionTypeIDs = _4(scheduledSectionsForCourse)
      .filter((sec: ISection) => this.ngScheduledSectionStatusServant.isStatusWaitlisted(sec) && sec.id !== section.id)
      .map('section_type')
      .value();

    const getTemplateForRequiredSection = (sectionType: ISectionType, additionalText = '') => {
      const sectionTypeString = `${_4.truncate(sectionType.name, { length: 25 })}${
        sectionType.is_required ? ' (required) ' : ''
      }`;
      return sectionTypeString || additionalText
        ? `<p class="text-sz-m mbz"> - ${sectionTypeString} ${additionalText} </p>`
        : ``;
    };

    const alertContent = _4.reduce(
      sectionTypesForCourse,
      (concat: string, sectionType: ISectionType) => {
        if (_4.includes(waitListedSectionTypeIDs, sectionType.id)) {
          return concat + getTemplateForRequiredSection(sectionType, '(Waitlisted)');
        }
        if (_4.includes(unScheduledSectionTypeIDsForCourse, sectionType.id)) {
          return concat + getTemplateForRequiredSection(sectionType);
        }
        return concat;
      },
      ``
    );

    if (!_4.isEmpty(alertContent)) {
      this.notificationDelegate.info({
        content: `<p class='text-sz-m text-bold'> ${RegistrationAlertMessages.ADDITIONAL_SECTIONS_ARE_REQUIRED_TEXT} </p> ${course.cd} ${course.title} ${alertContent}`,
        showIcon: true,
        dismissOnTimeout: false,
        iconClass: 'eab-icon-information'
      });
    }
  }

  showOtherRequiredSectionsAlert(pusherOutcome: IPusherOutcome[]) {
    const sectionsInPusherOutcome = _4.compact(
      _4.map(pusherOutcome, (outcome: IPusherOutcome) => {
        const section = _4.find(this.ngScheduledSectionsQuery.result.list, (sec: ISection) => sec.id === outcome.id);
        return section;
      })
    );
    const coursesInPusherOutcome = _4.uniq(_4.map(sectionsInPusherOutcome, (section: ISection) => section._course));

    const alertContent = _4.compact(
      _4.map(coursesInPusherOutcome, (course: ITermCourse) => {
        if (TermCourseServant.isMultiSectionCourse(course)) {
          const sectionsForCourse = _4.filter(this.ngScheduledSectionsQuery.result.list, (section: ISection) =>
            _4.isEqual(section.course, course.id)
          );

          const sectionTypesNotRegistered = _4(sectionsForCourse)
            .filter((section: ISection) =>
              _4.includes(
                [
                  ScheduledSectionStatus.UNSCHEDULED,
                  ScheduledSectionStatus.SECTION_PICKED,
                  ScheduledSectionStatus.WAITLISTED
                ],
                section.status
              )
            )
            .uniqBy('section_type')
            .sortBy('_section_type.rank')
            .value();

          if (_4.size(sectionTypesNotRegistered) > 0) {
            return _4.reduce(
              sectionTypesNotRegistered,
              (concat: string, section: ISection) => {
                return `${concat} <p class="text-sz-m mbz">${this.sectionTypeTemplate(section)}</p>`;
              },
              `${this.courseTemplate(sectionTypesNotRegistered[0])}`
            );
          } else {
            return '';
          }
        } else {
          return '';
        }
      })
    );
    if (!_4.isEmpty(alertContent)) {
      this.notificationDelegate.info({
        content: `<p class='text-sz-m text-bold'> ${
          RegistrationAlertMessages.ADDITIONAL_SECTIONS_ARE_REQUIRED_TEXT
        } </p> ${alertContent.join(' ')}`,
        showIcon: true,
        dismissOnTimeout: false,
        iconClass: 'eab-icon-information'
      });
    }
  }

  showHasRequisitesRegistrationAlerts(courseRequisites: any) {
    this.showRequisiteAlerts(courseRequisites);
  }

  showHasRequisitesWaitlistAlert(courseRequisite: any) {
    this.showRequisiteAlerts(courseRequisite);
  }

  showEnforcedCoreqsDropReminder(sections: ISection[]) {
    const sectionsCoreqs = _4.map(sections, this.courseTemplate);
    this.notificationDelegate.info({
      content: `<p class='text-sz-m'>${sectionsCoreqs.join(' and ')} ${
        RegistrationAlertMessages.ENFORCED_CO_REQUISITES_DROP_REMINDER_TEXT
      }</p>`,
      showIcon: true,
      dismissOnTimeout: true,
      iconClass: 'eab-icon-information'
    });
  }

  showAutoRegisterWaitlistedReminder() {
    this.notificationDelegate.info({
      content: `<p class='text-sz-m text-bold'> ${RegistrationAlertMessages.AUTO_REGISTER_WAITLISTED_TEXT} </p>`,
      showIcon: true,
      dismissOnTimeout: false,
      iconClass: 'eab-icon-information'
    });
  }

  showUnScheduleFailAlert(sectionId: number) {
    const section = _4.find(this.ngScheduledSectionsQuery.result.list, (sec: ISection) => sec.id === sectionId);
    const alertMessage = `<p class="text-sz-m mbz"> ${this.courseTemplate(section)} ${this.sectionTypeTemplate(
      section
    )} </p>`;
    const contentTemplate = `<p class="text-sz-m">${RegistrationAlertMessages.UNSCHEDULE_SECTION_FAIL_TEXT} </p>`;
    const config = {
      content: `${contentTemplate} ${alertMessage} please try again.`,
      showIcon: true,
      status: 'error',
      dismissButton: false
    };
    this.notificationDelegate.create(config);
  }
  showUnScheduleAllSectionsFailAlert(sections: ISection[]) {
    const alertMessage: string = _4.reduce(
      sections,
      (concat: string, section: ISection) =>
        `${concat} <p class="text-sz-m mbz"> ${this.sectionTypeTemplate(section)} </p>`,
      `${this.courseTemplate(sections[0])}`
    );
    const contentTemplate = `<p class="text-sz-m">${RegistrationAlertMessages.UNSCHEDULE_SECTION_FAIL_TEXT} </p>`;
    const config = {
      content: `${contentTemplate} ${alertMessage} please try again.`,
      showIcon: true,
      status: 'error',
      dismissButton: false
    };
    this.notificationDelegate.create(config);
  }

  private courseTemplate(section: ISection) {
    if (!section) {
      return '';
    }
    return section._course ? `${section._course.cd} ${section._course.title}` : ``;
  }

  private courseTemplateFromCourse(course: ICourse) {
    if (!course) {
      return '';
    }
    return `${course.cd} ${course.title}`;
  }

  private sectionTypeTemplate(section: ISection) {
    const isMultiSection = TermCourseServant.isMultiSectionCourse(section._course);
    const isWaitlisted = this.ngScheduledSectionStatusServant.isStatusWaitlisted(section);
    const waitlistedMessage = isWaitlisted ? `(Waitlisted)` : ``;
    const sectionType = isMultiSection ? this.ngMultiSectionServant.getTruncatedSectionTypeName(section, 18) : ``;
    const optional = isMultiSection && !section._section_type.is_required ? ` (optional) ` : ` `;
    return sectionType || waitlistedMessage ? `- ${sectionType}${optional}${waitlistedMessage}` : ``;
  }

  private courseTemplateFromPusherStatus(dropPusherOutcome: IPusherOutcome[]) {
    // SORT by section type rank
    const mappedSections = _4.sortBy(
      _4.compact(
        _4.map(dropPusherOutcome, (outcome: IPusherOutcome) => {
          const section = _4.find(this.ngScheduledSectionsQuery.result.list, (sec: ISection) => sec.id === outcome.id);
          return section;
        })
      ),
      [
        (section: ISection) => section.course,
        (section: ISection) => (section._section_type ? section._section_type.rank : 0)
      ]
    );

    const courses = _4.uniq(_4.compact(_4.map(mappedSections, '_course')));

    let alertMessage = '';

    alertMessage = _4.reduce(
      courses,
      (msg: string, course: ICourse) => {
        msg += `<p class="text-sz-m mbz"> ${this.courseTemplateFromCourse(course)} </p>`;
        const sections = _4.filter(mappedSections, (section: ISection) => _4.get(section, '_course.id') === course.id);
        const sectonsTemplate = _4.reduce(
          sections,
          (result: string, section: ISection) =>
            (result += `<p class="text-sz-m mbz"> ${this.sectionTypeTemplate(section)} </p>`),
          ''
        );
        msg += sectonsTemplate;
        return msg;
      },
      ''
    );

    return alertMessage;
  }

  private getContentListForRequisites(requisites: any) {
    const requisiteList = _4.map(requisites, (requisite: any) => {
      return `<div class='text-sz-m'>- ${requisite.cd} ${requisite.title}</div>`;
    });
    return requisiteList.join(' ');
  }

  private getAlertContentForRequisite(requisite: any) {
    const missingCoReqs =
      requisite && !_4.isEmpty(requisite.missing_coreqs)
        ? `<p class='text-sz-m'> ${RegistrationAlertMessages.CO_REQUISITES_REQUIRED_TEXT} ${requisite.fullTitle} </p>
    ${this.getContentListForRequisites(requisite.missing_coreqs)} `
        : ``;
    const missingPreReqs =
      requisite && !_4.isEmpty(requisite.missing_prereqs)
        ? `<p class='text-sz-m'> ${RegistrationAlertMessages.PRE_REQUISITES_REQUIRED_TEXT} ${requisite.fullTitle} </p>
    ${this.getContentListForRequisites(requisite.missing_prereqs)} `
        : ``;
    return `${missingCoReqs}${missingPreReqs}`;
  }
  private showRequisiteAlerts(courseRequisites: any) {
    const courseReqs = _4.isArray(courseRequisites) ? courseRequisites : [courseRequisites];
    const alertContent = _4(courseReqs)
      .uniqBy('courseId')
      .map((courseReq: any) => this.getAlertContentForRequisite(courseReq))
      .compact()
      .value()
      .join(' ');
    if (!_4.isEmpty(alertContent)) {
      this.notificationDelegate.info({
        content: `${alertContent}`,
        showIcon: true,
        dismissOnTimeout: false,
        iconClass: 'eab-icon-information'
      });
    }
  }
}

angular.module('capability-ng-scheduler').service('ngRegistrationAlertsServant', RegistrationAlertsServant);



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-scheduler/src/views/course_scheduler/servants/registration_alerts_servant.ts